import { isDeState } from "./holidays.js";
import { z } from "zod";

/**
 * Einkauf: Lieferanten, Bestellungen an Lieferanten (PDF per Mail), automatische Anlieferungsanmeldung bei Logiful.
 */

/** Anlieferadresse eines Lagers (pflegt Logiful) – steht auf jeder Bestellung */
export const deliveryAddressSchema = z.object({
  name: z.string().trim().min(1, "Name fehlt").max(120),
  street: z.string().trim().min(1, "Straße fehlt").max(120),
  zip: z.string().trim().min(2, "PLZ fehlt").max(12),
  city: z.string().trim().min(1, "Ort fehlt").max(80),
  country: z.string().trim().length(2).toUpperCase().default("DE"),
  /** Anlieferzeiten, z. B. „Mo–Fr 7–15 Uhr“ */
  hours: z.string().trim().max(200).nullable().default(null),
  /** Hinweise für Lieferanten, z. B. „Avis-Nummer außen auf jedes Packstück“ */
  notes: z.string().trim().max(600).nullable().default(null),
  /** Bundesland des Standorts (z. B. DE-NI) – für die Feiertage im Kalender der Kunden */
  region: z.string().refine(isDeState, "Unbekanntes Bundesland").nullable().default(null),
});
export type DeliveryAddress = z.infer<typeof deliveryAddressSchema>;

export const deliveryAddressesSchema = z.object({
  default: deliveryAddressSchema.nullable().default(null),
  /** je Lager (FFN warehouseId) abweichend */
  byWarehouse: z.record(z.string(), deliveryAddressSchema).default({}),
});
export type DeliveryAddresses = z.infer<typeof deliveryAddressesSchema>;

export const deliveryAddressFor = (a: DeliveryAddresses, warehouseId: string | null | undefined): DeliveryAddress | null => (warehouseId && a.byWarehouse[warehouseId]) || a.default;

const optText = (max: number) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));

export const supplierSchema = z.object({
  name: z.string().trim().min(1, "Name fehlt").max(120),
  /** Bestellungen gehen an diese Adresse */
  email: z.string().trim().toLowerCase().email("Bitte eine gültige E-Mail-Adresse").nullable().optional().transform((v) => v ?? null),
  contactName: optText(120),
  phone: optText(40),
  /** eure Kundennummer beim Lieferanten */
  customerNumber: optText(60),
  /** Anschrift des Lieferanten (mehrzeilig) */
  address: optText(400),
  /** Tage von Bestellung bis Einlagerung (sonst Standard aus Nachbestellung) */
  leadDays: z.number().int().min(0).max(365).nullable().optional().transform((v) => v ?? null),
  language: z.enum(["de", "en"]).default("de"),
  note: optText(1000),
});
export type SupplierInput = z.infer<typeof supplierSchema>;

/** Angaben des Bestellers (Händler) auf der Bestellung */
export const purchasingSettingsSchema = z.object({
  /** Firmenname auf der Bestellung (Standard: Name in Connect) */
  companyName: z.string().trim().max(120).nullable().default(null),
  /** Anschrift des Bestellers (mehrzeilig) */
  companyAddress: z.string().trim().max(400).nullable().default(null),
  /** Rückfragen/Bestätigungen des Lieferanten gehen hierhin (Standard: Person, die bestellt) */
  contactEmail: z.string().trim().toLowerCase().email("Bitte eine gültige E-Mail-Adresse").nullable().default(null),
  /** Text unter jeder Bestellung (Zahlungsbedingungen, Hinweise) */
  terms: z.string().trim().max(1000).nullable().default(null),
});
export type PurchasingSettings = z.infer<typeof purchasingSettingsSchema>;

export const poLineSchema = z.object({
  productId: z.string().uuid(),
  quantity: z.number().int().positive().max(1_000_000),
  /** Einkaufspreis je Stück (netto, EUR) – optional */
  unitPrice: z.number().min(0).max(1_000_000).nullable().optional().transform((v) => v ?? null),
});

export const PO_STATUS_LABEL = { draft: "Entwurf", sent: "bestellt", cancelled: "storniert" } as const;
export type PoStatus = keyof typeof PO_STATUS_LABEL;
